import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { type SurveyModel, type Question } from "survey-core";
import type { LoginDetailsFormValues } from "shared/components/survey-js-form/questions/login-details/LoginDetailsQuestion";
import {
  customErrorMessage,
  createValidateLoginDetailsQuestion,
  getLoginDetailsValidationState,
  updateAdditionalProfileContactInfoInForm,
  debouncedCheckUsername,
} from "shared/components/survey-js-form/questions/login-details/LoginDetailsQuestionUtils";
import { useCustomSession } from "shared/context/CustomSessionContext";

export const custom_question_login_details = "login_details";
const LoginDetailsQuestionComponent = (question: Question) => {
  const existingValue: LoginDetailsFormValues = {
    user_name: question.value?.user_name ?? "",
    password: question.value?.password ?? "",
    password_confirm: question.value?.password_confirm ?? "",
    old_password: question.value?.old_password ?? "",
  };

  const session = useCustomSession();
  const [formValues, setFormValues] =
    useState<LoginDetailsFormValues>(existingValue);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [confirmPasswordReadOnly, setConfirmPasswordReadOnly] = useState(false);
  const [isUsernameUnique, setIsUsernameUnique] = useState(true);
  const [usernameLookupError, setUsernameLookupError] = useState<
    string | undefined
  >();
  const initialEmailRef = useRef(existingValue.user_name);
  const originalReadOnlyRef = useRef<Map<Question, boolean>>(new Map());

  const survey = question.getSurvey() as SurveyModel;
  const existingProfile = session.profile;

  if (question.changePasswordTitle) {
    question.title = question.changePasswordTitle;
  }

  const oldPasswordInputVisible =
    session.societyUserId !== undefined &&
    !session.adminMode &&
    session.iat! < DateTime.now().minus({ days: 1 }).toSeconds();

  const passwordRequired = session.societyUser === undefined;
  const confirmPasswordRequired = session.profile === undefined;

  // An e-mail we could not verify is as blocking as one already taken: the
  // account would fail to be created either way.
  const canContinuePastUsername = isUsernameUnique && !usernameLookupError;

  useEffect(() => {
    const validateLoginDetailsQuestion = createValidateLoginDetailsQuestion({
      question,
      passwordRequired,
      confirmPasswordRequired,
      oldPasswordRequired: oldPasswordInputVisible,
    });

    question.errorLocation = "bottom";
    survey?.onValidateQuestion.add(validateLoginDetailsQuestion);
    survey.showNavigationButtons = canContinuePastUsername;

    survey.getAllQuestions().forEach((q: Question) => {
      if (q.name === custom_question_login_details) {
        return;
      }
      if (!canContinuePastUsername) {
        if (!originalReadOnlyRef.current.has(q)) {
          originalReadOnlyRef.current.set(q, q.readOnly);
        }
        q.readOnly = true;
      } else if (originalReadOnlyRef.current.has(q)) {
        q.readOnly = originalReadOnlyRef.current.get(q)!;
        originalReadOnlyRef.current.delete(q);
      }
    });

    return () => {
      survey?.onValidateQuestion.remove(validateLoginDetailsQuestion);
    };
  }, [
    survey,
    question,
    canContinuePastUsername,
    oldPasswordInputVisible,
    passwordRequired,
    confirmPasswordRequired,
  ]);

  const handleChanged = (nextFormValues: LoginDetailsFormValues) => {
    const nextValuesWithVisibilityApplied: LoginDetailsFormValues =
      oldPasswordInputVisible
        ? {
            user_name: nextFormValues.user_name ?? "",
            password: nextFormValues.password ?? "",
            password_confirm: nextFormValues.password_confirm ?? "",
            old_password: nextFormValues.old_password ?? "",
          }
        : {
            user_name: nextFormValues.user_name ?? "",
            password: nextFormValues.password ?? "",
            password_confirm: nextFormValues.password_confirm ?? "",
          };

    if (
      nextValuesWithVisibilityApplied.user_name &&
      !existingProfile?.societyUser
    ) {
      debouncedCheckUsername(
        session.societyId!,
        nextValuesWithVisibilityApplied.user_name,
        setIsUsernameUnique,
        setUsernameLookupError,
      );
    } else {
      setIsUsernameUnique(true);
      setUsernameLookupError(undefined);
    }

    const validationState = getLoginDetailsValidationState(
      nextValuesWithVisibilityApplied,
      {
        passwordRequired,
        confirmPasswordRequired,
        oldPasswordRequired: oldPasswordInputVisible,
      },
    );

    const finalValues: LoginDetailsFormValues = {
      ...validationState.normalizedValues,
      ...(oldPasswordInputVisible
        ? { old_password: nextFormValues.old_password ?? "" }
        : {}),
    };

    setFormValues(finalValues);
    setValidationErrors(validationState.validationErrors);
    setConfirmPasswordReadOnly(validationState.confirmPasswordReadOnly);

    question.value = finalValues;

    if (finalValues.user_name) {
      updateAdditionalProfileContactInfoInForm({
        question,
        values: finalValues,
      });
    }
  };

  return (
    <div className="sd-container-modern pr-2">
      <div className="flex flex-col gap-4 py-2">
        {!existingProfile?.institution && (
          <>
            <div className="flex flex-col">
              <h5 className="sd-title sd-element__title sd-question__title pb-1">
                <span
                  data-testid="login-details-email-label"
                  className="sv-string-viewer sv-string-viewer--multiline"
                >
                  Email
                </span>
              </h5>
              <input
                name="user_name"
                data-testid="login-details-email"
                type="email"
                className="sd-input sd-text !w-full"
                value={formValues.user_name}
                onChange={(e) =>
                  handleChanged({
                    ...formValues,
                    user_name: e.target.value,
                  })
                }
                disabled={
                  !!existingProfile?.societyUser && !!initialEmailRef.current
                }
              />
            </div>

            {usernameLookupError ? (
              <p
                className="text-status-danger-tint text-xs font-bold"
                data-testid="email-lookup-error"
              >
                {usernameLookupError}
              </p>
            ) : (
              !isUsernameUnique &&
              customErrorMessage({
                question,
                userHubUrl: session.society?.societySettingsPublic?.userHubUrl,
              })
            )}
          </>
        )}

        <div className="flex gap-4">
          {oldPasswordInputVisible && (
            <div className="flex-1">
              <div className="flex flex-col">
                <h5 className="sd-title sd-element__title sd-question__title pb-1">
                  <span
                    data-testid="login-details-old-password-label"
                    className="sv-string-viewer sv-string-viewer--multiline"
                  >
                    Old Password
                  </span>
                </h5>
                <input
                  name="old_password"
                  type="password"
                  data-testid="login-details-old-password"
                  autoComplete="current-password"
                  className="sd-input sd-text !w-full"
                  value={formValues.old_password ?? ""}
                  onChange={(e) =>
                    handleChanged({
                      ...formValues,
                      old_password: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          )}

          <div
            className={`flex-1 ${!canContinuePastUsername ? "opacity-25" : ""}`}
          >
            <div className="flex flex-col">
              <h5 className="sd-title sd-element__title sd-question__title pb-1">
                <span
                  data-testid="login-details-password-label"
                  className="sv-string-viewer sv-string-viewer--multiline"
                >
                  {oldPasswordInputVisible ? "New Password" : "Password"}
                </span>
              </h5>
              <input
                name="password"
                data-testid="login-details-password"
                type="password"
                autoComplete="new-password"
                className="sd-input sd-text !w-full"
                value={formValues.password ?? ""}
                onChange={(e) =>
                  handleChanged({
                    ...formValues,
                    password: e.target.value,
                  })
                }
                disabled={!canContinuePastUsername}
              />
            </div>
          </div>

          <div
            className={`flex-1 ${!canContinuePastUsername ? "opacity-25" : ""}`}
          >
            <div className="flex flex-col">
              <h5 className="sd-title sd-element__title sd-question__title pb-1">
                <span
                  data-testid="login-details-confirm-password-label"
                  className="sv-string-viewer sv-string-viewer--multiline"
                >
                  {oldPasswordInputVisible
                    ? "Confirm New Password"
                    : "Confirm Password"}
                </span>
              </h5>
              <input
                name="password_confirm"
                data-testid="login-details-confirm-password"
                type="password"
                autoComplete="new-password"
                className="sd-input sd-text !w-full"
                value={formValues.password_confirm ?? ""}
                onChange={(e) =>
                  handleChanged({
                    ...formValues,
                    password_confirm: e.target.value,
                  })
                }
                disabled={confirmPasswordReadOnly || !canContinuePastUsername}
              />
            </div>
          </div>
        </div>

        {validationErrors.length > 0 && (
          <div className="flex flex-col gap-1">
            {validationErrors.map((error) => (
              <p
                key={error}
                className="text-md font-bold text-status-danger-tint"
              >
                {error}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginDetailsQuestionComponent;
